import { forwardRef, useCallback, useContext } from 'react';
import { createI13nNode, I13nContext } from 'react-i13n';
import { Button } from '@tinderapp/ui-react';

import '@tinderapp/ui-react/css/Button/styles.css';
import '@tinderapp/ui-react/css/LoadingSpinner/styles.css';
import { I13N_TYPE_CLICK } from 'Lib/constants/i13nSpec';
import preventDefault from 'Lib/utils/preventDefault';
import useNavigate from 'Hooks/useNavigate';

import { BUTTON_THEMES, BUTTON_VARIANTS } from './constants';

type NavigateProps = React.ButtonHTMLAttributes<HTMLAnchorElement | HTMLButtonElement> &
  React.ComponentProps<typeof Button> & {
    to?: string;
    theme?: keyof typeof BUTTON_THEMES;
    i13n?: boolean;
    variant?: keyof typeof BUTTON_VARIANTS;
  };

export const ButtonNavigate = forwardRef<React.ComponentRef<typeof Button>, NavigateProps>(
  (props, ref) => {
    const { to, onClick, href, disabled, theme, i13n = true, variant, ...restProps } = props;
    const themeProps = theme ? BUTTON_THEMES[theme] : undefined;
    const variantProps = variant ? BUTTON_VARIANTS[variant] : undefined;
    const colorProps = themeProps || variantProps;

    const i13nContext = useContext(I13nContext);

    const navigate = useNavigate();

    const trackClickEvent = useCallback(
      (e) => {
        if (!i13n) {
          return;
        }
        const { executeEvent, i13nNode } = i13nContext;
        executeEvent(I13N_TYPE_CLICK, { i13nNode, e });
      },
      [i13n, i13nContext]
    );

    const handleClickButton = useCallback(
      (e) => {
        if (onClick) {
          onClick(e);
        }
        if (!e.isDefaultPrevented()) {
          trackClickEvent(e);
        }
      },
      [onClick, trackClickEvent]
    );

    const handleClickNavigate = useCallback(
      (e) => {
        if (onClick) {
          onClick(e);
        }
        if (!e.isDefaultPrevented()) {
          // Condition to be consistent with I13nLink, which does not fire click event
          if (!to) {
            trackClickEvent(e);
          }

          // react-router navigation
          if (to) {
            navigate(to);
            e.preventDefault();
            e.stopPropagation();
          }
        }
      },
      [onClick, to, navigate, trackClickEvent]
    );

    let handleClick: undefined | typeof handleClickNavigate | typeof preventDefault;
    if (disabled && (href || to)) {
      handleClick = preventDefault;
    } else if (onClick || href || to) {
      handleClick = handleClickNavigate;
    } else {
      handleClick = handleClickButton;
    }

    const buttonProps = {
      ...colorProps,
      ...restProps,
      disabled,
      onClick: handleClick,
      href: to || href || undefined,
    };

    return <Button {...buttonProps} ref={ref} />;
  }
);

// preventing react-i13n's innerRef prop from propagating down to ButtonNavigate
const ButtonNavigateOmitRef = ({
  innerRef,
  ...rest
}: React.ComponentProps<typeof ButtonNavigate> & {
  innerRef: React.RefCallback<unknown>;
}) => <ButtonNavigate {...rest} />;

const I13nButton = createI13nNode(
  ButtonNavigateOmitRef,
  {},
  {
    skipUtilFunctionsByProps: true,
  }
);

export default I13nButton;
